import { motion } from 'framer-motion';
import React, { Fragment, ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Hotkeys from 'react-hot-keys';
import { DFormEntity } from '../../api/fetchDoctorGenieByOID';
import PatientInfoComp, { decodeAge } from '../../pages/PatientInfoComp';
import { addPatient, fetchPtDir, NewPatient } from '../../store/actions/app';
import { updatePatient } from '../../store/actions/prescriptionPad';
import {
    getDoc,
    getItemFromMisc,
    getMyClinics,
    getPrescriptionById,
    getSelectedClinicId,
    MISC_KEYS,
} from '../../store/idbHandler';
import { getPtFullName, Patient } from '../../store/types/patient';
import { GenieSettings } from '../../api/pushGenie';
import updatePtIdInAppointment from '../../api/updatePtIdInAppointment';
import { captureError } from '../../pages/captureError';
import {
    checkIfValidPhoneNumber,
    getAddPDForms,
    getPatientPicUrl,
    uploadPatientPic,
} from '../../pages/utils';
import fetchPtMedHistory from '../../api/fetchPtMedHistory';
import { MHC } from '../../store/types/medicalHistory';
import { getGenieSetting } from '../../store/selectors/session';
import { GlobalStateV1 } from '../../store/reducers';
import { PulseLoader } from './Icons';
import { parsePhoneNumber } from 'libphonenumber-js';
import { getMyDbId } from '../../api/mactiveid';
import { getPatient } from '../../store/selectors/prescriptionPad';
import pushPtMedHistory from '../../api/pushPtMedHistory';
import { notification } from 'antd';

type ConfigTabs = 'Patient' | 'Medical History';

const EditPatientDetailsDialog: React.FC<{
    editPatient: Patient;
    pxId?: string;
    visitId?: string;
    onComplete?: (patient: Patient) => void;
    closeMe: () => void;
    ptMedicalHistory?: MHC | undefined;
    docid?: string;
}> = ({ docid, editPatient, pxId, onComplete, closeMe, visitId, ptMedicalHistory }) => {
    const dispatch = useDispatch();
    const rxPatient = useSelector(getPatient);

    const [dForm, setDForm] = useState<DFormEntity[]>([]);
    const [isLoading, setLoading] = useState(false);
    const [isMHLoading, setMhLoading] = useState(false);
    const [mh, setMH] = useState<MHC>({});
    const isAbdmEnabled = useSelector((state: GlobalStateV1) =>
        getGenieSetting(state, 'abdm'),
    ) as boolean;

    const [configureTab, setConfigureTab] = useState<{
        isConfigureDialogVisible: boolean;
        selectedTab: ConfigTabs;
    }>({
        isConfigureDialogVisible: false,
        selectedTab: 'Patient',
    });

    useEffect(() => {
        getAddPDForms().then(setDForm).catch(console.error);

        if (!ptMedicalHistory) {
            setMhLoading(true);

            fetchPtMedHistory(getDoc()?._id ?? '', editPatient?.localId ?? '')
                .then((ptMH) => {
                    setMhLoading(false);
                    setMH(ptMH.data ?? {});
                })
                .catch((error) => {
                    setMhLoading(false);
                    console.log(error);
                    captureError(error as Error);
                });
        } else {
            setMH(ptMedicalHistory);
        }
    }, []);

    const onSave = async (newP: NewPatient, pic?: File, mh?: MHC) => {
        setLoading(true);

        const newPatient = {
            ...newP,
            localId: editPatient.localId,
            oid: editPatient.id,
        };

        const onSuccess = async (patient: Patient & { apiFailure: boolean; error?: any }) => {
            await new Promise((resolve) => setTimeout(resolve, 2000));

            if (mh) {
                await pushPtMedHistory(
                    docid || getDoc()?._id || '',
                    patient.id || patient.localId,
                    mh,
                    new Date().toISOString(),
                );
            }
            setLoading(false);

            if (patient.localId !== editPatient.localId) {
                // update pt dir
                fetchPtDir();
            }

            if (pxId) {
                const px = await getPrescriptionById(pxId);
                if (patient.localId !== editPatient.localId) {
                    if (!px) {
                        return;
                    }
                    try {
                        await updatePtIdInAppointment(px.visitId, patient.id);
                    } catch (e) {
                        captureError(e as Error);
                    }
                }
                const rxNewPt = {
                    id: patient.id,
                    localId: patient.id || patient.localId,
                    profile: {
                        personal: {
                            name: getPtFullName(patient),
                            phone: patient.personal.phone,
                            age: patient.personal.age,
                            gender: patient.personal.gender,
                            // TODO
                            bloodgroup: editPatient.bloodgroup,
                        },
                    },
                    formData: patient.formData,
                    relations: rxPatient?.relations,
                };
                dispatch(
                    updatePatient(rxNewPt, {
                        ...px,
                        patient: rxNewPt,
                    }),
                );
            } else if (visitId) {
                // prescription is not locally created on this yet
                if (patient.localId !== editPatient.localId) {
                    try {
                        await updatePtIdInAppointment(visitId, patient.id);
                    } catch (e) {
                        captureError(e as Error);
                    }
                }
            }
            onComplete && onComplete(patient);
            patient.id && uploadPatientPic(patient.id, pic);
            closeMe();
            return;
        };

        const settings = await getItemFromMisc<GenieSettings>(
            getMyDbId() || '',
            MISC_KEYS.settings,
        );

        dispatch(
            addPatient({
                newPatient,
                sendWelcome: settings.sendWelcome,
                onSuccess,
                oldPtObj: editPatient,
                doc: docid ? { id: docid } : undefined,
                onError: (error) => {
                    setLoading(false);
                    notification.error({
                        message: 'Error in editing patient',
                        description: error,
                    });
                },
            }),
        );

        return;
    };

    const actions = {
        onCancel: closeMe,
        onSave: onSave,
        isLoading,
    };

    const setConfigureActionDialog = (visible: boolean, tab?: ConfigTabs): void => {
        setConfigureTab({
            isConfigureDialogVisible: visible,
            selectedTab: tab ?? 'Patient',
        });
    };

    const isAbhaOnboarded = getMyClinics()?.find((c) => c._id === getSelectedClinicId())?.abdm
        ?.hfr_id;

    return (
        <Fragment>
            <Hotkeys keyName="esc" onKeyDown={closeMe}>
                {isMHLoading ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        exit={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        style={{ zIndex: 20 }}
                        transition={{ duration: 0.2, ease: 'linear' }}
                        className="flex font-lato"
                    >
                        <div onClick={closeMe} className="modal-overlay"></div>
                        <div className="modal-content w-60p h-5/6 overflow-auto">
                            <div className="flex flex-col bg-white rounded-8 h-full overflow-hidden">
                                <div className="flex items-center justify-between p-16 bg-neutral-9 rounded-t-8">
                                    <p className="font-700 text-neutral-1 text-18">
                                        Edit Patient Details
                                    </p>
                                    <div
                                        onClick={closeMe}
                                        className="flex items-center justify-center w-24 h-24"
                                    >
                                        <svg
                                            width="20"
                                            height="20"
                                            viewBox="0 0 320 512"
                                            className="cursor-pointer fill-neutral-5"
                                        >
                                            <path d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                                        </svg>
                                    </div>
                                </div>
                                <div className="flex justify-center items-center h-full">
                                    <PulseLoader />
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ) : (
                    <PatientInfoComp
                        patientEditFlow={true}
                        pic={getPatientPicUrl(editPatient.id)}
                        data={{
                            name: [editPatient.personal.name.f, editPatient.personal.name.l]
                                .filter(Boolean)
                                .join(' '),
                            phCode: editPatient.personal.phone?.c || '',
                            phoneNumber: editPatient.personal.phone?.n,
                            phCodeEnum:
                                editPatient.personal.phone?.c && editPatient.personal.phone?.n
                                    ? parsePhoneNumber(
                                          (
                                              editPatient.personal.phone.c! +
                                              editPatient.personal.phone.n!
                                          ).trim(),
                                      ).country ?? 'IN'
                                    : 'IN',
                            ...decodeAge(editPatient.personal.age),

                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            gender: editPatient.personal.gender,
                            isPhoneNumberError: editPatient.personal.phone?.n
                                ? !checkIfValidPhoneNumber(
                                      editPatient.personal.phone?.c || '',
                                      editPatient.personal.phone?.n,
                                  )
                                : false,
                            isDobError: false,
                            isAgeDOBError: false,
                            isNameError: false,
                            isGenderError: false,
                            isPhoneInputDisabled: false,
                            isNameInputDisabled: false,

                            dForm,
                            dfValues:
                                editPatient.formData?.reduce(
                                    (agg, df) => ({ ...agg, [df.key]: df.value }),
                                    {},
                                ) || {},
                        }}
                        FillerComp={FillerComp}
                        ActionBar={ActionBar}
                        actions={actions}
                        actionBarProps={{ isLoading }}
                        medicalHistory={mh}
                        isDialogVisible={configureTab.isConfigureDialogVisible}
                        setDialogVisibility={setConfigureActionDialog}
                        configureTab={configureTab.selectedTab}
                        showMHConfigure={false}
                        key={JSON.stringify(mh)}
                        showUseABDM={!!isAbhaOnboarded}
                        isAbdmEnabled={isAbdmEnabled}
                        formState="add-pt"
                        patientId={editPatient.localId}
                    />
                )}
            </Hotkeys>
        </Fragment>
    );
};

const ActionBar: React.FC<{ onCancel: () => void; onSave: () => void; isLoading?: boolean }> = ({
    onCancel,
    onSave,
    isLoading,
}) => {
    return (
        <div className="flex items-center justify-between p-16 border-t border-neutral-7">
            <div>
                <button onClick={onCancel} className="neutral-btn  px-12 py-8 btn--small ">
                    <span className="w-full text-center">Cancel</span>
                </button>
            </div>
            <div>
                <button
                    onClick={onSave}
                    className="primary-btn  px-12 py-8 btn--small fill space-x-8"
                    disabled={isLoading}
                >
                    {isLoading && (
                        <svg
                            viewBox="0 0 512 512"
                            className="fill-white animate-spin"
                            width="20"
                            height="20"
                        >
                            <path d="M456.433 371.72l-27.79-16.045c-7.192-4.152-10.052-13.136-6.487-20.636 25.82-54.328 23.566-118.602-6.768-171.03-30.265-52.529-84.802-86.621-144.76-91.424C262.35 71.922 256 64.953 256 56.649V24.56c0-9.31 7.916-16.609 17.204-15.96 81.795 5.717 156.412 51.902 197.611 123.408 41.301 71.385 43.99 159.096 8.042 232.792-4.082 8.369-14.361 11.575-22.424 6.92z" />
                        </svg>
                    )}
                    <span className="w-full text-center">Update</span>
                </button>
            </div>
        </div>
    );
};

const FillerComp: React.FC<{
    Form: ReactNode;
    ActionBar: ReactNode;
    closeMe: () => void;
    formClassName?: string;
}> = ({ Form, ActionBar, closeMe, formClassName }) => {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ zIndex: 20 }}
            transition={{ duration: 0.2, ease: 'linear' }}
            className="flex font-lato"
        >
            <div onClick={closeMe} className="modal-overlay"></div>
            <div className="modal-content w-60p h-5/6 overflow-auto">
                <div className="flex flex-col bg-white rounded-8 h-full overflow-hidden">
                    <div className="flex items-center justify-between p-16 bg-neutral-9 rounded-t-8">
                        <p className="font-700 text-neutral-1 text-18">Edit Patient Details</p>
                        <div
                            onClick={closeMe}
                            className="flex items-center justify-center w-24 h-24"
                        >
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 320 512"
                                className="cursor-pointer fill-neutral-5"
                            >
                                <path d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"></path>
                            </svg>
                        </div>
                    </div>
                    <div className="w-full border-b border-neutral-7"></div>
                    <div
                        className={`${
                            formClassName ?? 'flex-auto h-full p-16 space-y-24 overflow-auto'
                        }`}
                    >
                        {Form}
                        <div className="h-24"></div>
                    </div>

                    <div>{ActionBar}</div>
                </div>
            </div>
        </motion.div>
    );
};

export default EditPatientDetailsDialog;
